import {
  useMediaQuery,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { CurrencyFormat } from "@securitize/reactjs-sid-utils";
import { useMemo } from "react";
import TokenAvatar from "../../TokenAvatar";
import StyledCard from "../../../StyledCard";
import { Translate } from "../../../../../Translation/Translate";
import translationKeys from "../../../../../../contexts/I18nContext/translationKeys";

export default function AssetPreviouslyOwnedCard({
  tokenId,
  tokenName,
  tokenIcon,
  tokenSymbol,
  issuerLogo,
  networkIcon,
  totalUnits,
  lastPrice,
  totalValue,
  issuerMainCurrency,
}: {
  tokenId: string;
  tokenName: string;
  tokenIcon: string;
  tokenSymbol: string;
  issuerLogo: string;
  networkIcon: string;
  totalUnits: number;
  lastPrice: string;
  totalValue: string;
  issuerMainCurrency: string;
}) {
  const totalValueFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: issuerMainCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [issuerMainCurrency],
  );

  const currencyFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: issuerMainCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      }),
    [issuerMainCurrency],
  );

  const totalUnitsFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  return (
    <StyledCard key={tokenId} to={`/asset-details/${tokenId}`}>
      <WhmStack gap="8px">
        {isMobile ? (
          <WhmStack direction="row" gap="16px">
            <WhmStack direction="row" gap="8px" sx={{ flex: 2 }}>
              <TokenAvatar
                tokenIcon={tokenIcon}
                issuerLogo={issuerLogo}
                networkIcon={networkIcon}
              />
              <WhmStack>
                <WhmTooltip title={tokenName}>
                  <WhmTypography
                    fontWeight={500}
                    sx={{
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tokenName}
                  </WhmTypography>
                </WhmTooltip>
                <WhmTypography
                  sx={{
                    color: "text.secondary",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {totalUnitsFormat.formatNumber(totalUnits)} {tokenSymbol}
                </WhmTypography>
              </WhmStack>
            </WhmStack>

            <WhmStack sx={{ textAlign: "right" }}>
              <WhmTypography>
                {totalValueFormat.format(totalValue)}
              </WhmTypography>
            </WhmStack>
          </WhmStack>
        ) : (
          <WhmStack direction="row" gap="16px" sx={{ alignItems: "center" }}>
            <WhmStack
              direction="row"
              gap="8px"
              sx={{ flex: 2, alignItems: "center" }}
            >
              <TokenAvatar
                tokenIcon={tokenIcon}
                issuerLogo={issuerLogo}
                networkIcon={networkIcon}
              />
              <WhmTooltip title={tokenName}>
                <WhmTypography
                  fontWeight={500}
                  sx={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {tokenName}
                </WhmTypography>
              </WhmTooltip>
            </WhmStack>

            <WhmStack
              direction="row"
              gap="8px"
              sx={{ flex: 4, minWidth: "450px" }}
            >
              <WhmStack sx={{ flex: 1 }}>
                <WhmTypography variant="body2" color="text.secondary">
                  <Translate
                    translationKey={
                      translationKeys.PORTFOLIO_MY_ASSETS_ASSET_UNITS
                    }
                  />
                </WhmTypography>
                <WhmTypography>
                  {totalUnitsFormat.formatNumber(totalUnits)}
                </WhmTypography>
              </WhmStack>
              <WhmStack sx={{ flex: 1 }}>
                <WhmTypography variant="body2" color="text.secondary">
                  <Translate
                    translationKey={
                      translationKeys.PORTFOLIO_MY_ASSETS_ASSET_PRICE
                    }
                  />
                </WhmTypography>
                <WhmTypography>
                  {currencyFormat.format(lastPrice)}
                </WhmTypography>
              </WhmStack>
              <WhmStack sx={{ flex: 1 }}>
                <WhmTypography variant="body2" color="text.secondary">
                  <Translate
                    translationKey={
                      translationKeys.PORTFOLIO_MY_ASSETS_ASSET_TOTAL_VALUE
                    }
                  />
                </WhmTypography>
                <WhmTypography>
                  {totalValueFormat.format(totalValue)}
                </WhmTypography>
              </WhmStack>
            </WhmStack>
          </WhmStack>
        )}
      </WhmStack>
    </StyledCard>
  );
}
